/**
 * Native push prerequisite probe + local delivery record (Card 17).
 *
 * The critical, easy-to-miss half of push diagnostics: answering "can this build receive a
 * push AT ALL?" before answering anything about tokens or permissions.
 *
 * `GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md` §13.6 documents what happens without this. A Capacitor
 * plugin that ships no `Package.swift` is omitted from the iOS binary while `cap sync` still
 * LISTS it under "Found N plugins for ios"; the npm package installs, the imports resolve,
 * the app compiles, and only at runtime does iOS reject every call as unimplemented. That
 * state cost this project a full day and two wrong diagnoses, because a missing plugin and a
 * user with nothing to show produced the same observable result.
 *
 * There is no JavaScript API for "is google-services.json present". Config presence can only
 * be INFERRED — from a registration attempt failing in a characteristic way, or from a token
 * having been obtained at all. So `firebaseConfigured` is deliberately tri-state: `null`
 * means "not yet determined", which is the honest answer before any attempt has been made.
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { isNative, isPluginAvailable, platform } from '@/utils/platform';
import type {
  LocalDeliveryRecord,
  LocalTokenState,
  NativePushPrerequisites,
  NativePushUnavailableReason,
  PushPlatform
} from '@/types/pushDiagnostics.types';

/** Capacitor's registered-plugin name. Must match the plugin's `@CapacitorPlugin` name. */
const PLUGIN_NAME = 'PushNotifications';

// Written by `sendTokenToServer` in pushNotifications.ts — read here, not owned here.
const TOKEN_KEY = 'push_token';
const TOKEN_PENDING_SYNC_KEY = 'push_token_pending_sync';

// Owned by this module.
const LAST_DELIVERY_KEY = 'push_last_delivery';
const LAST_REGISTRATION_ERROR_KEY = 'push_last_registration_error';

/** Must match TOKEN_SUFFIX_LENGTH in the backend's push-notification-workflow.ts. */
const TOKEN_SUFFIX_LENGTH = 12;

/**
 * A native call that reaches an unregistered plugin. Capacitor surfaces this as
 * "not implemented" / UNIMPLEMENTED depending on platform and version, so match loosely.
 */
const PLUGIN_MISSING_PATTERN = /not implemented|unimplemented|no such module|plugin .* not found/i;

/**
 * The characteristic shapes of "the Firebase config file is not in this build".
 * Android throws on the FirebaseApp itself; iOS complains about the plist or about APNs
 * never handing back a token.
 */
const FIREBASE_CONFIG_MISSING_PATTERN =
  /firebaseapp is not initialized|google-services|googleservice-info|missing_instanceid_service|fis_auth_error|service_not_available|default app has not been configured|no valid ["']?google/i;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A full or disabled store must not break push. Diagnostics degrade, delivery does not.
  }
}

// ---------------------------------------------------------------------------
// Registration errors — the only evidence we get that Firebase config is absent
// ---------------------------------------------------------------------------

export interface RegistrationErrorRecord {
  message: string;
  at: string;
  /** The native build that recorded it. Null on records written before this was stamped. */
  build?: string | null;
}

/**
 * Persist the last native registration failure.
 *
 * Persisted rather than held in memory because the WebView is torn down on app kill, and the
 * failure happens at launch — by the time someone opens the diagnostics screen the in-memory
 * copy from THIS session may be the only one, and on a cold start there would be none at all.
 */
export function recordRegistrationError(error: unknown, build?: string | null): void {
  const message =
    (error as any)?.message ??
    (typeof error === 'string' ? error : JSON.stringify(error ?? null));

  safeSet(
    LAST_REGISTRATION_ERROR_KEY,
    JSON.stringify({
      message: String(message).slice(0, 500),
      at: new Date().toISOString(),
      // WHICH BUILD FAILED. Without this the record is un-falsifiable: localStorage survives an
      // app update, so a failure reported by an old binary keeps being served as evidence about
      // a new one. See the staleness check in probeNativePushPrerequisites.
      build: build ?? null
    })
  );
}

export function getLastRegistrationError(): RegistrationErrorRecord | null {
  const raw = safeGet(LAST_REGISTRATION_ERROR_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.message !== 'string') return null;
    return { message: parsed.message, at: parsed.at ?? '', build: parsed.build ?? null };
  } catch {
    return null;
  }
}

/**
 * The running native build number (`CFBundleVersion` / `versionCode`), or null off-native.
 * Cached: it cannot change while the process lives, and this sits on the launch path.
 */
let cachedNativeBuild: string | null | undefined;

export async function getNativeBuild(): Promise<string | null> {
  if (cachedNativeBuild !== undefined) return cachedNativeBuild;
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    cachedNativeBuild = info?.build ?? null;
  } catch {
    // Web, or the plugin is unavailable. Either way there is no build to compare against.
    cachedNativeBuild = null;
  }
  return cachedNativeBuild;
}

/** Clear the stored failure once registration succeeds, so the screen stops reporting it. */
export function clearRegistrationError(): void {
  try {
    localStorage.removeItem(LAST_REGISTRATION_ERROR_KEY);
  } catch {
    // Ignore — see safeSet.
  }
}

// ---------------------------------------------------------------------------
// Last delivery seen on THIS device
// ---------------------------------------------------------------------------

/**
 * Record that a notification actually arrived here.
 *
 * The server can only report what it SENT. "FCM accepted the message" and "the phone in your
 * hand showed a banner" are different claims, and the gap between them (a dropped Android
 * channel, a silent APNs failure) is exactly where push bugs live.
 */
export function recordDeliverySeen(record: {
  title?: string | null;
  body?: string | null;
  via: 'foreground' | 'tap';
}): void {
  const entry: LocalDeliveryRecord = {
    title: record.title ?? null,
    body: record.body ?? null,
    via: record.via,
    receivedAt: new Date().toISOString()
  };

  safeSet(LAST_DELIVERY_KEY, JSON.stringify(entry));
}

export function getLastDeliverySeen(): LocalDeliveryRecord | null {
  const raw = safeGet(LAST_DELIVERY_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.receivedAt !== 'string') return null;
    return {
      title: parsed.title ?? null,
      body: parsed.body ?? null,
      via: parsed.via === 'tap' ? 'tap' : 'foreground',
      receivedAt: parsed.receivedAt
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Local token state
// ---------------------------------------------------------------------------

/**
 * What this device believes about its own registration.
 *
 * Native only. Web used to report the service worker's push subscription here, but the PWA
 * and its worker are retired (Card 30) and a browser has no registration to report — so web
 * is simply never registered.
 */
export async function getLocalTokenState(): Promise<LocalTokenState> {
  if (!isNative) {
    return { present: false, suffix: null, pendingSync: false };
  }

  const token = safeGet(TOKEN_KEY);
  return {
    present: Boolean(token),
    suffix: token ? token.slice(-TOKEN_SUFFIX_LENGTH) : null,
    pendingSync: safeGet(TOKEN_PENDING_SYNC_KEY) === 'true'
  };
}

// ---------------------------------------------------------------------------
// The probe
// ---------------------------------------------------------------------------

function classifyRegistrationError(message: string): NativePushUnavailableReason {
  if (PLUGIN_MISSING_PATTERN.test(message)) return 'plugin_missing';
  if (FIREBASE_CONFIG_MISSING_PATTERN.test(message)) return 'firebase_config_missing';
  return 'registration_failed';
}

/**
 * Determine whether this build can receive native push, and if not, why.
 *
 * Two independent signals, because neither alone is sufficient:
 *   1. `Capacitor.isPluginAvailable()` plus a real call to the plugin. The call matters —
 *      §13.6's SPM trap is precisely the case where everything static looks fine and only a
 *      runtime call reveals the plugin is absent from the binary.
 *   2. The last recorded registration failure, which is the only way a missing
 *      `google-services.json` / `GoogleService-Info.plist` ever announces itself.
 */
export async function probeNativePushPrerequisites(): Promise<NativePushPrerequisites> {
  const currentPlatform = platform as PushPlatform;

  if (!isNative) {
    // Web is not "broken" — it is a different transport. `reason: null` keeps the resolver
    // from reporting a misconfigured build on a platform that has no native build.
    return {
      platform: currentPlatform,
      supported: false,
      pluginRegistered: false,
      firebaseConfigured: null,
      reason: null,
      detail: null
    };
  }

  const declared = isPluginAvailable(PLUGIN_NAME);

  // Actually call the plugin. A plugin that is listed but not built answers here and nowhere
  // else — this single call is what would have caught the biometric bug in §13.6 on day one.
  let callable = false;
  let callError: string | null = null;
  try {
    await PushNotifications.checkPermissions();
    callable = true;
  } catch (error: any) {
    callError = String(error?.message ?? error);
  }

  if (!declared || (!callable && callError && PLUGIN_MISSING_PATTERN.test(callError))) {
    return {
      platform: currentPlatform,
      supported: false,
      pluginRegistered: false,
      firebaseConfigured: null,
      reason: 'plugin_missing',
      detail: callError ?? `Capacitor reports no registered "${PLUGIN_NAME}" plugin in this build.`
    };
  }

  const storedError = getLastRegistrationError();
  const hasToken = Boolean(safeGet(TOKEN_KEY));

  // A FAILURE REPORTED BY A DIFFERENT BUILD IS NOT EVIDENCE ABOUT THIS ONE.
  //
  // This record lives in localStorage, which survives an app update — the WebView origin does not
  // change. So a genuine build defect (say a binary signed without `aps-environment`) recorded a
  // failure, the defect was fixed, the user updated, and the app kept reporting "this app build
  // cannot receive notifications" from the new, correct binary. It could never recover on its own:
  // `supported: false` suppresses the permission prompt, so `register()` is never reached, so the
  // `registration` listener never fires, so `clearRegistrationError()` never runs. One bad launch
  // disabled push permanently, across updates. Reported from the field on 2026-08-22.
  //
  // Records written before the build stamp existed have `build == null`; those are discarded too,
  // because they carry exactly the same ambiguity.
  const runningBuild = await getNativeBuild();
  const isStale =
    storedError !== null && (storedError.build == null || storedError.build !== runningBuild);

  if (isStale) {
    clearRegistrationError();
  }

  const registrationError = isStale ? null : storedError;

  if (registrationError) {
    const reason = classifyRegistrationError(registrationError.message);
    return {
      platform: currentPlatform,
      supported: false,
      pluginRegistered: true,
      firebaseConfigured: reason === 'firebase_config_missing' ? false : null,
      reason,
      detail: registrationError.message
    };
  }

  return {
    platform: currentPlatform,
    supported: true,
    pluginRegistered: true,
    // A token can only exist if Firebase/APNs handed one over, so its presence is proof.
    // Its absence proves nothing yet — hence null rather than false.
    firebaseConfigured: hasToken ? true : null,
    reason: null,
    detail: callError
  };
}
